import { Autocomplete, Box, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContextInterface } from '../IContext';

export type ArticleType = 'advertisement' | 'literary text' | 'letter/email' | 'article' | 'short description';
type LanguageVariant = 'traditional' | 'simplified';
type QuestionType = 'mc';

class GcseRcMcContextClass implements GcseRcMcContext {
    type: 'gcseRcMc';
    theme?: string;
    topic?: string;
    article_type?: ArticleType;
    question_count?: number;
    language_variant?: LanguageVariant;
    question_type?: QuestionType;

    constructor(context: Pick<GcseRcMcContext, 'type' | 'theme' | 'topic' | 'article_type' | 'question_count' | 'language_variant' | 'question_type'>) {
        this.type = context.type;
        this.theme = context.theme;
        this.topic = context.topic;
        this.article_type = context.article_type;
        this.question_count = context.question_count;
        this.language_variant = context.language_variant;
        this.question_type = context.question_type;
    }

    isSufficient(): boolean {
        return !!this.topic && !!this.theme && !!this.article_type && !!this.language_variant && !!this.question_count && !!this.question_type;
    }
}

export interface GcseRcMcContext extends ContextInterface {
    type: 'gcseRcMc';
    theme?: string;
    topic?: string;
    article_type?: ArticleType;
    question_count?: number;
    language_variant?: LanguageVariant;
    question_type?: QuestionType;
}

export const RCPanel = ({ value, onChange }: { value: GcseRcMcContext; onChange?: (value: GcseRcMcContext) => void }) => {
    const { t } = useTranslation();

    const [theme, setTheme] = useState<string>(value?.theme || '');
    const [topic, setTopic] = useState<string>(value?.topic || '');
    const [articleType, setArticleType] = useState<ArticleType>(value?.article_type || 'advertisement');
    const [questionType, setQuestionType] = useState<QuestionType>(value?.question_type || 'mc');
    const [languageVariant, setLanguageVariant] = useState<LanguageVariant>(value?.language_variant || 'traditional');
    const [questionCount, setQuestionCount] = useState<number>(value?.question_count || 1);

    const themeOptions = [
        // sample themes
        {
            category: 'GCSE',
            title: 'Identity and culture',
            sub: [{ title: 'Who am I' }, { title: 'Daily life' }, { title: 'Cultural life' }],
        },
        {
            category: 'GCSE',
            title: 'Local area, holiday and travel',
            sub: [{ title: 'Holidays' }, { title: 'Travel and tourist transactions' }, { title: 'Town, region and country' }],
        },
        {
            category: 'GCSE',
            title: 'School',
            sub: [{ title: 'What school is like' }, { title: 'School activities' }],
        },
        {
            category: 'GCSE',
            title: 'Future aspirations, study and work',
            sub: [{ title: 'Using languages beyond the classroom' }, { title: 'Ambitions' }, { title: 'Work' }],
        },
        {
            category: 'GCSE',
            title: 'International and global dimension',
            sub: [{ title: 'Bringing the world together' }, { title: 'Environmental issues' }],
        },
    ];

    return (
        <Stack spacing={2}>
            <Box>
                <Autocomplete
                    aria-label='rc theme'
                    fullWidth
                    freeSolo
                    options={themeOptions}
                    groupBy={(option) => option.category}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
                    value={theme}
                    onChange={(_, newValue) => {
                        const themeValue = typeof newValue === 'string' ? newValue : newValue?.title || '';
                        setTheme(themeValue);
                        onChange?.(
                            new GcseRcMcContextClass({
                                type: 'gcseRcMc',
                                theme: themeValue,
                                topic: topic,
                                article_type: articleType,
                                language_variant: languageVariant,
                                question_count: questionCount,
                                question_type: questionType,
                            }),
                        );

                        setTopic('');
                        onChange?.(
                            new GcseRcMcContextClass({
                                type: 'gcseRcMc',
                                theme: themeValue,
                                topic: '',
                                article_type: articleType,
                                language_variant: languageVariant,
                                question_count: questionCount,
                                question_type: questionType,
                            }),
                        );
                    }}
                    onInputChange={(_, newInputValue) => {
                        setTheme(newInputValue);
                        onChange?.(
                            new GcseRcMcContextClass({
                                type: 'gcseRcMc',
                                theme: newInputValue,
                                topic: topic,
                                article_type: articleType,
                                language_variant: languageVariant,
                                question_count: questionCount,
                                question_type: questionType,
                            }),
                        );
                    }}
                    renderInput={(params) => <TextField {...params} placeholder={t('experiment.generator.question.label.theme')} label={t('experiment.generator.question.label.theme')} />}
                />
            </Box>
            <Box>
                <Autocomplete
                    aria-label='rc topic'
                    fullWidth
                    freeSolo
                    options={themeOptions.find((opt) => opt.title === theme)?.sub || []}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
                    value={topic}
                    onChange={(_, newValue) => {
                        const topicValue = typeof newValue === 'string' ? newValue : newValue?.title || '';
                        setTopic(topicValue);
                        onChange?.(
                            new GcseRcMcContextClass({
                                type: 'gcseRcMc',
                                theme: theme,
                                topic: topicValue,
                                article_type: articleType,
                                language_variant: languageVariant,
                                question_count: questionCount,
                                question_type: questionType,
                            }),
                        );
                    }}
                    onInputChange={(_, newInputValue) => {
                        setTopic(newInputValue);
                        onChange?.(
                            new GcseRcMcContextClass({
                                type: 'gcseRcMc',
                                theme: theme,
                                topic: newInputValue,
                                article_type: articleType,
                                language_variant: languageVariant,
                                question_count: questionCount,
                                question_type: questionType,
                            }),
                        );
                    }}
                    renderInput={(params) => <TextField {...params} placeholder={t('experiment.generator.question.label.topic')} label={t('experiment.generator.question.label.topic')} />}
                />
            </Box>
            <Box>
                <FormControl fullWidth>
                    <InputLabel>{t('experiment.generator.question.label.articleType')}</InputLabel>
                    <Select
                        label={t('experiment.generator.question.label.articleType')}
                        aria-label='rc article type'
                        value={articleType}
                        onChange={(e) => {
                            setArticleType(e.target.value as ArticleType);
                            onChange?.(
                                new GcseRcMcContextClass({
                                    type: 'gcseRcMc',
                                    theme: theme,
                                    topic,
                                    article_type: e.target.value as ArticleType,
                                    language_variant: languageVariant,
                                    question_count: questionCount,
                                    question_type: questionType,
                                }),
                            );
                        }}
                    >
                        <MenuItem value='advertisement'>Advertisement</MenuItem>
                        <MenuItem value='article'>Article</MenuItem>
                        <MenuItem value='letter/email'>Letter/Email</MenuItem>
                        <MenuItem value='literary text'>Literary Text</MenuItem>
                        <MenuItem value='short description'>Short Description</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Box>
                <FormControl fullWidth>
                    <InputLabel>{t('experiment.generator.question.label.languageVariant')}</InputLabel>
                    <Select
                        label={t('experiment.generator.question.label.languageVariant')}
                        value={languageVariant}
                        onChange={(e) => {
                            setLanguageVariant(e.target.value as LanguageVariant);
                            onChange?.(
                                new GcseRcMcContextClass({
                                    type: 'gcseRcMc',
                                    theme: theme,
                                    topic,
                                    article_type: articleType,
                                    language_variant: e.target.value as LanguageVariant,
                                    question_count: questionCount,
                                    question_type: questionType,
                                }),
                            );
                        }}
                    >
                        <MenuItem value='traditional'>{t('experiment.generator.question.languageVariant.traditional')}</MenuItem>
                        <MenuItem value='simplified'>{t('experiment.generator.question.languageVariant.simplified')}</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Box>
                <TextField
                    label={t('experiment.generator.question.label.questionCount')}
                    fullWidth
                    type='number'
                    aria-label='rc question count'
                    placeholder={t('experiment.generator.question.label.questionCount')}
                    value={questionCount === 0 ? '' : questionCount}
                    slotProps={{
                        htmlInput: {
                            min: 1,
                            max: 10,
                        },
                    }}
                    onChange={(e) => {
                        let count = 0;
                        if (Number(e.target.value) <= 0) {
                            setQuestionCount(count);
                        } else {
                            count = Number(e.target.value);
                            setQuestionCount(count > 10 ? 10 : count);
                        }
                        onChange?.(
                            new GcseRcMcContextClass({
                                type: 'gcseRcMc',
                                topic,
                                theme: theme,
                                article_type: articleType,
                                language_variant: languageVariant,
                                question_count: count,
                                question_type: questionType,
                            }),
                        );
                    }}
                />
            </Box>
            <Box>
                <FormControl fullWidth>
                    <InputLabel>{t('experiment.generator.question.label.questionFormat')}</InputLabel>
                    <Select
                        label={t('experiment.generator.question.label.questionFormat')}
                        value={questionType}
                        onChange={(e) => {
                            setQuestionType(e.target.value as QuestionType);
                            onChange?.(
                                new GcseRcMcContextClass({
                                    type: 'gcseRcMc',
                                    theme: theme,
                                    topic,
                                    article_type: articleType,
                                    language_variant: languageVariant,
                                    question_count: questionCount,
                                    question_type: e.target.value as QuestionType,
                                }),
                            );
                        }}
                    >
                        <MenuItem value='mc'>{t('experiment.generator.question.type.mc')}</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        </Stack>
    );
};
